import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge, Button, Card, EmptyState, Input, Select, Table, Textarea, type TableColumn, type Tone } from "../../../components/ui";
import Modal from "../../../components/base/Modal";
import { isValidRfc, normalizeRfc } from "../../../utils/rfc";
import {
  exchangeApi,
  apiErrorMessage,
  type CarrierInvite,
  type CarrierInviteInput,
  type CarrierInviteList,
  type CarrierInviteResult,
  type CarrierInviteStage,
  type CarrierInviteStatus,
} from "../exchange/api";
import { fmtDateTime, relative } from "../exchange/format";
import { ErrorState, Kpi, Loader, Section } from "../exchange/components";
import { PhoneField, isValidPhone, joinPhone } from "../exchange/PhoneField";
import { BATCH_MAX, downloadTemplate, parseDelimited, parseMatrix, readBatchFile, type ParsedBatch } from "../lib/carrierImport";

// ===========================================================================
// Cargo Market — Administración › Alta de transportistas.
// Transportistas que pueden volverse clientes de telemetría: se dan de alta uno
// por uno o por lote. Cada uno recibe el correo de bienvenida con la liga que lo
// lleva directo a Transportista › Verificación (su ficha y su expediente), y
// queda como lead + negocio en el CRM. El backend (cargoCarrierInviteService)
// exige requirePlatformAdmin; un 403 se muestra como «sólo admins».
// ===========================================================================

const STATUS_META: Record<CarrierInviteStatus, { label: string; tone: Tone; hint: string }> = {
  queued: { label: "En cola", tone: "neutral", hint: "Se está creando su cuenta" },
  provisioning: { label: "Creando", tone: "info", hint: "Se está creando su cuenta" },
  provisioned: { label: "Alta lista", tone: "success", hint: "Cuenta, CRM y correo listos" },
  partial: { label: "Con pendientes", tone: "warning", hint: "Algún paso falló: revisa el detalle" },
  "existing-account": { label: "Ya tenía cuenta", tone: "info", hint: "Se le mandó la liga para entrar con su cuenta" },
  failed: { label: "Falló", tone: "error", hint: "No se creó la cuenta: puedes reintentar" },
};

const STAGE_META: Record<CarrierInviteStage, { label: string; tone: Tone; icon: string }> = {
  invited: { label: "Invitado", tone: "neutral", icon: "ri-mail-send-line" },
  opened: { label: "Entró", tone: "info", icon: "ri-login-box-line" },
  profile: { label: "Ficha completa", tone: "primary", icon: "ri-profile-line" },
  verified: { label: "Verificado", tone: "success", icon: "ri-shield-check-line" },
  tracked: { label: "Rastreado", tone: "success", icon: "ri-map-pin-line" },
};
const STAGE_ORDER: CarrierInviteStage[] = ["invited", "opened", "profile", "verified", "tracked"];

const RESULT_LABELS: Record<string, string> = {
  queued: "En cola",
  provisioned: "Alta lista",
  partial: "Con pendientes",
  "existing-account": "Ya tenía cuenta",
  failed: "Falló",
  invalid: "Datos inválidos",
  duplicate: "Repetido",
  limit: "Tope diario",
};

const isPending = (i: CarrierInvite) => i.status === "queued" || i.status === "provisioning";

function Forbidden() {
  return (
    <EmptyState
      icon="ri-lock-line"
      title="Sólo administradores de la plataforma"
      description="Esta sección es para el equipo de WAYAK que da de alta transportistas en Cargo Market."
    />
  );
}

/** Barra de 5 pasos: hasta dónde llegó el transportista. */
function StageTrack({ stage }: { stage: CarrierInviteStage }) {
  const at = STAGE_ORDER.indexOf(stage);
  const meta = STAGE_META[stage] ?? STAGE_META.invited;
  return (
    <span className="inline-flex flex-col gap-1">
      <Badge size="sm" tone={meta.tone} className="w-fit">
        <i className={`${meta.icon} mr-1`} />
        {meta.label}
      </Badge>
      <span className="flex gap-0.5" aria-hidden>
        {STAGE_ORDER.map((s, i) => (
          <span key={s} className={`h-1 w-5 rounded-full ${i <= at ? "bg-primary-500" : "bg-subtle"}`} />
        ))}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Alta individual
// ---------------------------------------------------------------------------

function NewCarrierModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ legalName: "", tradeName: "", rfc: "", contactName: "", contactEmail: "", city: "", state: "", unitsCount: "", notes: "" });
  const [phone, setPhone] = useState({ code: "+52", number: "" });
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const errors = {
    legalName: !form.legalName.trim() ? "Indica la razón social" : null,
    contactName: !form.contactName.trim() ? "Indica el nombre del contacto" : null,
    contactEmail: !form.contactEmail.trim() ? "Indica el correo" : !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.contactEmail.trim()) ? "Correo inválido" : null,
    rfc: form.rfc.trim() && !isValidRfc(form.rfc) ? "RFC inválido" : null,
    phone: phone.number.trim() && !isValidPhone(phone.code, phone.number) ? "Teléfono inválido" : null,
    unitsCount: form.unitsCount.trim() && !/^\d{1,6}$/.test(form.unitsCount.trim()) ? "Escribe un número" : null,
  };
  const err = (k: keyof typeof errors) => (touched ? errors[k] ?? undefined : undefined);

  const submit = async () => {
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    const body: CarrierInviteInput = {
      legalName: form.legalName.trim(),
      tradeName: form.tradeName.trim() || null,
      rfc: form.rfc.trim() ? normalizeRfc(form.rfc) : null,
      contactName: form.contactName.trim(),
      contactEmail: form.contactEmail.trim().toLowerCase(),
      contactPhone: phone.number.trim() ? joinPhone(phone.code, phone.number) : null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      unitsCount: form.unitsCount.trim() ? Number(form.unitsCount) : null,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      const out = await exchangeApi.adminCreateCarrierInvite(body);
      const r = out.results[0];
      if (r?.status === "provisioned") toast.success(`${body.legalName}: alta lista y correo de bienvenida enviado`);
      else if (r?.status === "existing-account") toast.success(`${body.contactEmail} ya tenía cuenta: le enviamos la liga para entrar a su perfil`);
      else if (r?.status === "partial") toast.warning(`${body.legalName}: alta creada con pendientes (revisa el detalle)`);
      else {
        toast.error(r?.reason || RESULT_LABELS[r?.status ?? ""] || "No se pudo dar de alta");
        return;
      }
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Nuevo transportista"
      onClose={onClose}
      width="w-full max-w-3xl"
      content={
        <div className="space-y-4">
          <p className="text-sm text-content-muted">
            Creamos su empresa y su usuario, lo registramos en el CRM y le enviamos un correo para definir su contraseña y entrar directo a completar y verificar su perfil.
          </p>
          <h3 className="text-sm font-semibold border-b border-line pb-2">Empresa</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Razón social" required value={form.legalName} onChange={set("legalName")} error={err("legalName")} placeholder="Transportes del Norte SA de CV" />
            <Input label="Nombre comercial" value={form.tradeName} onChange={set("tradeName")} placeholder="TDN" />
            <Input label="RFC" value={form.rfc} onChange={set("rfc")} error={err("rfc")} hint="Opcional: lo puede capturar él" placeholder="TNO010101AB1" />
            <Input label="Unidades (aprox.)" inputMode="numeric" value={form.unitsCount} onChange={set("unitsCount")} error={err("unitsCount")} placeholder="12" />
            <Input label="Ciudad" value={form.city} onChange={set("city")} />
            <Input label="Estado" value={form.state} onChange={set("state")} />
          </div>
          <h3 className="text-sm font-semibold border-b border-line pb-2 mt-4">Contacto (recibe el correo)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Nombre" required value={form.contactName} onChange={set("contactName")} error={err("contactName")} />
            <Input label="Correo" required type="email" value={form.contactEmail} onChange={set("contactEmail")} error={err("contactEmail")} />
            <PhoneField label="Teléfono" code={phone.code} number={phone.number} onChange={setPhone} error={err("phone")} />
          </div>
          <Textarea label="Notas para el equipo comercial" rows={2} value={form.notes} onChange={set("notes")} placeholder="Rutas, tipo de carga, cómo lo conocimos…" />
          <div className="flex gap-3 border-t border-line pt-4">
            <Button variant="outline" shape="pill" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button shape="pill" className="flex-1" loading={saving} icon={<i className="ri-mail-send-line" />} onClick={() => void submit()}>Dar de alta y enviar correo</Button>
          </div>
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Carga por lote
// ---------------------------------------------------------------------------

function ResultList({ result }: { result: CarrierInviteResult }) {
  const skipped = result.results.filter((r) => ["invalid", "duplicate", "limit"].includes(r.status));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="En el archivo" value={result.total} icon="ri-file-list-3-line" tone="info" />
        <Kpi label="Dados de alta" value={result.created} icon="ri-checkbox-circle-line" tone="success" />
        <Kpi label="Omitidos" value={result.skipped} icon="ri-error-warning-line" tone={result.skipped ? "warning" : "primary"} />
      </div>
      {result.created > 0 && (
        <p className="text-sm text-content-muted">
          Las cuentas se están creando y los correos salen en unos momentos; el avance de cada uno se ve en la lista.
        </p>
      )}
      {skipped.length > 0 && (
        <Table
          compact
          columns={[
            { key: "row", header: "Fila", render: (r) => r.row },
            { key: "email", header: "Correo", render: (r) => r.email || "—" },
            { key: "status", header: "Resultado", render: (r) => <Badge size="sm" tone="warning">{RESULT_LABELS[r.status] ?? r.status}</Badge> },
            { key: "reason", header: "Motivo", render: (r) => r.reason || "—" },
          ]}
          rows={skipped}
          rowKey={(r) => String(r.row)}
        />
      )}
    </div>
  );
}

function BatchModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [batch, setBatch] = useState<ParsedBatch | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [pasted, setPasted] = useState("");
  const [reading, setReading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<CarrierInviteResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const valid = useMemo(() => (batch?.rows ?? []).filter((r) => r.errors.length === 0), [batch]);
  const withErrors = (batch?.rows.length ?? 0) - valid.length;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setReading(true);
    try {
      setBatch(await readBatchFile(file));
      setSource(file.name);
    } catch (e) {
      toast.error(apiErrorMessage(e, "No se pudo leer el archivo"));
    } finally {
      setReading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onPaste = () => {
    if (!pasted.trim()) return;
    setBatch(parseMatrix(parseDelimited(pasted)));
    setSource("Filas pegadas");
  };

  const send = async () => {
    if (!valid.length) return;
    setSending(true);
    try {
      const out = await exchangeApi.adminCreateCarrierInvitesBatch(valid.map((r) => r.input));
      setResult(out);
      if (out.created) toast.success(`${out.created} transportista(s) en proceso de alta`);
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const columns: TableColumn<ParsedBatch["rows"][number]>[] = [
    { key: "line", header: "Fila", render: (r) => r.line },
    {
      key: "company",
      header: "Transportista",
      render: (r) => (
        <span className="inline-flex flex-col">
          <span className="font-medium">{r.input.legalName || "—"}</span>
          {r.input.rfc && <span className="text-[11px] text-content-faint">{r.input.rfc}</span>}
        </span>
      ),
    },
    {
      key: "contact",
      header: "Contacto",
      render: (r) => (
        <span className="inline-flex flex-col">
          <span>{r.input.contactName || "—"}</span>
          <span className="text-[11px] text-content-faint">{r.input.contactEmail || "—"}</span>
        </span>
      ),
    },
    {
      key: "check",
      header: "Revisión",
      render: (r) =>
        r.errors.length ? (
          <span className="text-xs text-error">{r.errors.join(" · ")}</span>
        ) : (
          <Badge size="sm" tone="success">Lista</Badge>
        ),
    },
  ];

  return (
    <Modal
      title="Carga por lote de transportistas"
      onClose={onClose}
      width="w-full max-w-4xl"
      content={
        result ? (
          <div className="space-y-4">
            <ResultList result={result} />
            <div className="flex gap-3 border-t border-line pt-4">
              <Button variant="outline" shape="pill" className="flex-1" onClick={() => { setResult(null); setBatch(null); setPasted(""); setSource(null); }}>Cargar otro archivo</Button>
              <Button shape="pill" className="flex-1" onClick={onClose}>Listo</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Card padding="md" surface="app">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-content">1. Llena la plantilla</p>
                  <p className="text-xs text-content-muted mt-0.5">
                    Requeridos: razón social, contacto y correo. Opcionales: nombre comercial, RFC, teléfono, ciudad, estado, unidades y notas. Hasta {BATCH_MAX} por carga.
                  </p>
                </div>
                <Button size="sm" variant="outline" icon={<i className="ri-file-excel-2-line" />} onClick={() => void downloadTemplate()}>Descargar plantilla</Button>
              </div>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-content">2. Sube el archivo</span>
                <input ref={fileRef} type="file" accept=".xlsx,.csv,.txt" className="hidden" onChange={(e) => void onFile(e.target.files?.[0])} />
                <Button variant="dashed" loading={reading} icon={<i className="ri-upload-2-line" />} onClick={() => fileRef.current?.click()}>Elegir .xlsx o .csv</Button>
                <span className="text-xs text-content-faint">{source ? `Leído: ${source}` : "La primera fila debe traer los encabezados."}</span>
              </div>
              <div className="flex flex-col gap-1.5">
                <Textarea label="…o pega las filas desde Excel" rows={3} value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder={"Razón social\tContacto\tCorreo\nTransportes del Norte\tLuis Pérez\tluis@tdn.com.mx"} />
                <Button size="sm" variant="outline" disabled={!pasted.trim()} onClick={onPaste}>Revisar lo pegado</Button>
              </div>
            </div>

            {batch?.error && <EmptyState icon="ri-error-warning-line" title="Revisa el archivo" description={batch.error} />}
            {batch && !batch.error && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-content">3. Revisa antes de enviar:</span>
                  <Badge tone="success">{valid.length} listos</Badge>
                  {withErrors > 0 && <Badge tone="warning">{withErrors} con errores (no se envían)</Badge>}
                  {batch.ignored.length > 0 && <span className="text-xs text-content-faint">Columnas ignoradas: {batch.ignored.join(", ")}</span>}
                </div>
                <Table compact columns={columns} rows={batch.rows} rowKey={(r) => String(r.line)} />
              </div>
            )}

            <div className="flex gap-3 border-t border-line pt-4">
              <Button variant="outline" shape="pill" className="flex-1" onClick={onClose}>Cancelar</Button>
              <Button shape="pill" className="flex-1" disabled={!valid.length || Boolean(batch?.error)} loading={sending} icon={<i className="ri-mail-send-line" />} onClick={() => void send()}>
                {valid.length ? `Dar de alta ${valid.length} y enviar correos` : "Dar de alta"}
              </Button>
            </div>
          </div>
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Sección
// ---------------------------------------------------------------------------

function DetailModal({ invite, onClose }: { invite: CarrierInvite; onClose: () => void }) {
  const status = STATUS_META[invite.status] ?? STATUS_META.queued;
  return (
    <Modal
      title={invite.tradeName || invite.legalName}
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge tone={status.tone}>{status.label}</Badge>
            <StageTrack stage={invite.progress} />
          </div>
          <p className="text-content-muted">{status.hint}</p>
          <div className="space-y-1">
            {STAGE_ORDER.map((s) => (
              <div key={s} className="flex justify-between gap-3">
                <span className={invite.progressAt?.[s] ? "text-content" : "text-content-faint"}>
                  <i className={`${STAGE_META[s].icon} mr-1.5`} />
                  {STAGE_META[s].label}
                </span>
                <span className="text-content-muted">{invite.progressAt?.[s] ? fmtDateTime(invite.progressAt[s]!) : "—"}</span>
              </div>
            ))}
          </div>
          {invite.errors?.length > 0 && (
            <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 space-y-1">
              <p className="font-medium text-content">Pasos con problema</p>
              {invite.errors.map((e, i) => (
                <p key={i} className="text-xs text-content-secondary">
                  <span className="font-medium">{e.step}:</span> {e.message}
                </p>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 border-t border-line pt-4">
            {invite.refs.leadId && (
              <Button size="sm" variant="outline" icon={<i className="ri-contacts-book-line" />} onClick={() => window.open("/crm/contactos/bandeja", "_blank", "noopener")}>
                Lead en el CRM
              </Button>
            )}
            {invite.refs.crmDealId && (
              <Button size="sm" variant="outline" icon={<i className="ri-briefcase-4-line" />} onClick={() => window.open("/crm/negocios/negocios", "_blank", "noopener")}>
                Negocio en el CRM
              </Button>
            )}
          </div>
        </div>
      }
    />
  );
}

/** Carga con detección de 403 (el endpoint es sólo para admins de la plataforma). */
function useInvites() {
  const [data, setData] = useState<CarrierInviteList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const reload = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setError(null);
      setForbidden(false);
      setData(await exchangeApi.adminCarrierInvites());
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) setForbidden(true);
      else if (!quiet) setError(apiErrorMessage(err, "No se pudo cargar la información"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  // Mientras haya altas en cola (lote), se refresca sola cada 5 s.
  const pending = (data?.rows ?? []).some(isPending);
  useEffect(() => {
    if (!pending) return;
    const t = window.setInterval(() => void reload(true), 5000);
    return () => window.clearInterval(t);
  }, [pending, reload]);
  return { data, loading, error, forbidden, reload, setData };
}

export function AltaTransportistasAdminSection() {
  const q = useInvites();
  const [creating, setCreating] = useState(false);
  const [batching, setBatching] = useState(false);
  const [detail, setDetail] = useState<CarrierInvite | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState<"" | CarrierInviteStage | "issues">("");

  const replace = (inv: CarrierInvite) =>
    q.setData((prev) => (prev ? { ...prev, rows: prev.rows.map((r) => (r.id === inv.id ? inv : r)) } : prev));

  const resend = async (inv: CarrierInvite) => {
    setBusyId(inv.id);
    try {
      replace(await exchangeApi.adminResendCarrierInvite(inv.id));
      toast.success(`Correo reenviado a ${inv.contactEmail}`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };
  const retry = async (inv: CarrierInvite) => {
    setBusyId(inv.id);
    try {
      const out = await exchangeApi.adminRetryCarrierInvite(inv.id);
      replace(out);
      if (out.status === "failed") toast.error("Volvió a fallar: revisa el detalle");
      else toast.success(`${inv.legalName}: alta lista`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (q.data?.rows ?? []).filter((r) => {
      if (stage === "issues" && !(r.status === "failed" || r.status === "partial")) return false;
      if (stage && stage !== "issues" && r.progress !== stage) return false;
      if (!term) return true;
      return [r.legalName, r.tradeName, r.rfc, r.contactName, r.contactEmail].some((v) => String(v || "").toLowerCase().includes(term));
    });
  }, [q.data, search, stage]);

  const s = q.data?.summary;
  const reached = (from: CarrierInviteStage) =>
    STAGE_ORDER.slice(STAGE_ORDER.indexOf(from)).reduce((n, st) => n + (s?.byProgress?.[st] ?? 0), 0);

  const columns: TableColumn<CarrierInvite>[] = [
    {
      key: "company",
      header: "Transportista",
      render: (r) => (
        <span className="inline-flex flex-col">
          <span className="font-medium">{r.tradeName || r.legalName}</span>
          <span className="text-[11px] text-content-faint">{[r.tradeName ? r.legalName : null, r.rfc].filter(Boolean).join(" · ") || "Sin RFC"}</span>
        </span>
      ),
    },
    {
      key: "contact",
      header: "Contacto",
      render: (r) => (
        <span className="inline-flex flex-col">
          <span>{r.contactName}</span>
          <span className="text-[11px] text-content-faint">{r.contactEmail}</span>
        </span>
      ),
    },
    { key: "units", header: "Unidades", render: (r) => r.unitsCount ?? "—", hideOnMobile: true },
    {
      key: "status",
      header: "Alta",
      render: (r) => {
        const m = STATUS_META[r.status] ?? STATUS_META.queued;
        return <span title={m.hint}><Badge size="sm" tone={m.tone}>{isPending(r) && <i className="ri-loader-4-line animate-spin mr-1" />}{m.label}</Badge></span>;
      },
    },
    { key: "progress", header: "Avance", render: (r) => <StageTrack stage={r.progress} /> },
    {
      key: "email",
      header: "Correo",
      hideOnMobile: true,
      render: (r) =>
        r.email?.sent ? (
          <span title={r.email.sentAt ? fmtDateTime(r.email.sentAt) : undefined} className="text-content-muted">
            Enviado {relative(r.email.sentAt)}{r.email.sendCount > 1 ? ` · ${r.email.sendCount} envíos` : ""}
          </span>
        ) : r.email?.lastError ? (
          <span className="text-xs text-error" title={r.email.lastError}>No salió</span>
        ) : (
          <span className="text-content-faint">—</span>
        ),
    },
    {
      key: "created",
      header: "Alta",
      hideOnMobile: true,
      render: (r) => (
        <span className="inline-flex flex-col" title={fmtDateTime(r.createdAt)}>
          <span>{relative(r.createdAt) || "—"}</span>
          {r.createdByName && <span className="text-[11px] text-content-faint">{r.createdByName}{r.batchId ? " · lote" : ""}</span>}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        <span className="inline-flex gap-2" onClick={(e) => e.stopPropagation()}>
          {r.status === "failed" && !r.refs.companyId ? (
            <Button size="sm" variant="warningSoft" loading={busyId === r.id} disabled={!!busyId} icon={<i className="ri-restart-line" />} onClick={() => void retry(r)}>Reintentar</Button>
          ) : (
            <Button
              size="sm"
              variant="soft"
              loading={busyId === r.id}
              disabled={!!busyId || !r.refs.userId || isPending(r) || STAGE_ORDER.indexOf(r.progress) >= STAGE_ORDER.indexOf("profile")}
              icon={<i className="ri-mail-send-line" />}
              onClick={() => void resend(r)}
            >
              Reenviar
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <Section
      title="Alta de transportistas"
      subtitle="Da de alta transportistas (uno o por lote): reciben un correo de bienvenida que los lleva directo a completar y verificar su perfil, y quedan en el CRM como prospectos de telemetría."
      actions={
        !q.forbidden ? (
          <>
            <Button size="sm" variant="outline" icon={<i className="ri-refresh-line" />} onClick={() => void q.reload()}>Actualizar</Button>
            <Button size="sm" variant="outline" icon={<i className="ri-file-upload-line" />} onClick={() => setBatching(true)}>Carga por lote</Button>
            <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setCreating(true)}>Nuevo transportista</Button>
          </>
        ) : undefined
      }
      filters={
        !q.forbidden && (q.data?.rows.length ?? 0) > 0 ? (
          <div className="flex flex-col sm:flex-row gap-3">
            <Input containerClassName="sm:max-w-xs flex-1" placeholder="Buscar por empresa, RFC, contacto o correo" icon={<i className="ri-search-line" />} value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select
              containerClassName="sm:max-w-[14rem]"
              value={stage}
              onChange={(e) => setStage(e.target.value as typeof stage)}
              options={[
                { value: "", label: "Todas las etapas" },
                ...STAGE_ORDER.map((st) => ({ value: st, label: STAGE_META[st].label })),
                { value: "issues", label: "Con pendientes o fallidas" },
              ]}
            />
          </div>
        ) : undefined
      }
    >
      {q.forbidden ? (
        <Forbidden />
      ) : q.loading && !q.data ? (
        <Loader />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.reload()} />
      ) : (q.data?.rows ?? []).length === 0 ? (
        <EmptyState
          icon="ri-truck-line"
          title="Aún no das de alta transportistas"
          description="Agrega uno o sube la plantilla con varios. Cada uno recibe su correo de bienvenida y queda como lead en el CRM."
          action={<Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setCreating(true)}>Nuevo transportista</Button>}
        />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi label="Invitados" value={s?.total ?? 0} icon="ri-mail-send-line" tone="info" />
            <Kpi label="Entraron" value={reached("opened")} icon="ri-login-box-line" tone="info" />
            <Kpi label="Ficha completa" value={reached("profile")} icon="ri-profile-line" />
            <Kpi label="Verificados" value={reached("verified")} icon="ri-shield-check-line" tone="success" />
            <Kpi label="Con GPS WAYAK" value={reached("tracked")} icon="ri-map-pin-line" tone="success" hint="Clientes de telemetría" />
          </div>
          <Table columns={columns} rows={rows} rowKey={(r) => r.id} onRowClick={(r) => setDetail(r)} emptyText="Ningún transportista coincide con el filtro." />
        </div>
      )}
      {creating && <NewCarrierModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); void q.reload(true); }} />}
      {batching && <BatchModal onClose={() => setBatching(false)} onDone={() => void q.reload(true)} />}
      {detail && <DetailModal invite={detail} onClose={() => setDetail(null)} />}
    </Section>
  );
}
